const CAMPAIGN_STORAGE_KEY = 'dietz-promo-gift-2026-09-18-seen';
const OPEN_DELAY = 5000;

const readCampaignSeen = () => {
  try {
    return window.sessionStorage.getItem(CAMPAIGN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const markCampaignSeen = () => {
  try {
    window.sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, 'true');
  } catch {
    return;
  }
};

const initializePromoPopup = (root) => {
  if (root.dataset.promoPopupGiftInitialized === 'true' || readCampaignSeen()) return;

  const dialog = root.querySelector('[data-promo-popup-gift-dialog]');
  const closeButton = root.querySelector('[data-promo-popup-gift-close]');
  const dismissButton = root.querySelector('[data-promo-popup-gift-dismiss]');
  const video = root.querySelector('[data-promo-popup-gift-video]');
  const videoSource = root.querySelector('[data-promo-popup-gift-source]');
  const playButton = root.querySelector('[data-promo-popup-gift-play]');

  if (!(dialog instanceof HTMLDialogElement) || !(closeButton instanceof HTMLButtonElement)) return;

  root.dataset.promoPopupGiftInitialized = 'true';

  let playbackRetryUsed = false;

  const forceVideoSilent = () => {
    if (!(video instanceof HTMLVideoElement)) return;

    video.defaultMuted = true;
    video.muted = true;
    video.volume = 0;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };

  const hidePlayButton = () => {
    if (playButton instanceof HTMLButtonElement) playButton.hidden = true;
  };

  const showPlayButton = () => {
    if (playButton instanceof HTMLButtonElement && dialog.open) playButton.hidden = false;
  };

  const prepareVideo = () => {
    if (
      !(video instanceof HTMLVideoElement) ||
      !(videoSource instanceof HTMLSourceElement) ||
      videoSource.src
    ) {
      return;
    }

    const source = videoSource.dataset.src;
    if (!source) return;

    forceVideoSilent();

    // Preload during the five-second delay without letting the hidden dialog
    // consume the first playback before it is visible.
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.preload = 'auto';
    videoSource.src = source;
    video.load();
  };

  const enableSilentAutoplay = () => {
    if (!(video instanceof HTMLVideoElement)) return;

    forceVideoSilent();
    video.autoplay = true;
    video.setAttribute('autoplay', '');
  };

  const playVideo = () => {
    if (!(video instanceof HTMLVideoElement) || !dialog.open) return;

    enableSilentAutoplay();

    let playRequest;
    try {
      playRequest = video.play();
    } catch {
      showPlayButton();
      return;
    }

    if (!(playRequest instanceof Promise)) {
      hidePlayButton();
      return;
    }

    playRequest.then(hidePlayButton).catch(() => {
      if (!dialog.open) return;

      if (!playbackRetryUsed) {
        playbackRetryUsed = true;

        const retryPlayback = () => {
          if (!dialog.open) return;
          window.requestAnimationFrame(playVideo);
        };

        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
          window.setTimeout(retryPlayback, 160);
        } else {
          video.addEventListener('canplay', retryPlayback, { once: true });
        }

        return;
      }

      showPlayButton();
    });
  };

  const resetVideo = () => {
    if (!(video instanceof HTMLVideoElement)) return;

    video.pause();
    if (video.readyState > 0) video.currentTime = 0;
    hidePlayButton();
  };

  const closeDialog = () => {
    if (dialog.open) dialog.close();
  };

  const openDialog = () => {
    if (readCampaignSeen() || dialog.open) return;

    try {
      dialog.showModal();
    } catch {
      dialog.setAttribute('open', '');
    }

    document.body.classList.add('dietz-promo-gift-open');
    markCampaignSeen();

    if (video instanceof HTMLVideoElement) {
      if (video.readyState > 0) video.currentTime = 0;

      // Two animation frames give mobile WebKit time to paint the dialog before
      // evaluating its visibility for muted autoplay.
      window.requestAnimationFrame(() => window.requestAnimationFrame(playVideo));
    }
  };

  prepareVideo();
  const openTimer = window.setTimeout(openDialog, OPEN_DELAY);

  closeButton.addEventListener('click', closeDialog);
  dismissButton?.addEventListener('click', closeDialog);

  playButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    playbackRetryUsed = true;
    playVideo();
  });

  video?.addEventListener('playing', hidePlayButton);
  video?.addEventListener('error', showPlayButton);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });

  dialog.addEventListener('close', () => {
    resetVideo();
    document.body.classList.remove('dietz-promo-gift-open');
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && dialog.open && video?.paused && !video.ended) {
      playVideo();
    }
  });

  window.addEventListener('pagehide', () => window.clearTimeout(openTimer), { once: true });
};

document.querySelectorAll('[data-promo-popup-gift]').forEach(initializePromoPopup);
